// Cart + checkout HTTP wrappers. Pure functions on top of `fetchJson`;
// react-query hooks live next door in `hooks.ts`.

import { fetchJson } from "@/lib/api";
import type {
  Cart,
  CartItem,
  CartPayload,
  CartQuoteResponse,
  FinalizeRequest,
  FinalizeResponse
} from "./types";

const BASE = "/api/v1/cart";
const FINALIZE = "/api/v1/checkout/finalize";

// GET /api/v1/cart — lazily creates an anonymous cart and sets the
// signed `:jdr_cart` cookie on first call, then reuses the row across
// requests. After sign-in, the controller migrates the session row
// onto the user.
export async function getCart(): Promise<Cart> {
  return fetchJson<Cart>(BASE);
}

// PATCH /api/v1/cart — partial update of the non-item payload fields
// (delivery address, dates, window, payment method, consent, promo).
// Item edits go through itemsApi.* below.
export async function patchCart(patch: Partial<CartPayload>): Promise<Cart> {
  return fetchJson<Cart>(BASE, {
    method: "PATCH",
    body: patch as unknown as Record<string, unknown>
  });
}

export async function deleteCart(): Promise<null> {
  return fetchJson<null>(BASE, { method: "DELETE" });
}

export async function getCartQuote(): Promise<CartQuoteResponse> {
  return fetchJson<CartQuoteResponse>(`${BASE}/quote`);
}

// Phase 4c COI waiver bypass — single PATCH for the insurance step's
// chooser. Returns the success body `{ ok, cart: { id, damage_waiver_opted_in } }`.
export async function patchCartInsurance(opted_in: boolean): Promise<{ ok: boolean; cart: { id: number; damage_waiver_opted_in: boolean } }> {
  return fetchJson(`${BASE}/insurance`, {
    method: "PATCH",
    body: { damage_waiver_opted_in: opted_in } as Record<string, unknown>
  });
}

export const itemsApi = {
  add: (item: CartItem): Promise<Cart> =>
    fetchJson<Cart>(`${BASE}/items`, {
      method: "POST",
      body: item as unknown as Record<string, unknown>
    }),
  update: (index: number, patch: Partial<CartItem>): Promise<Cart> =>
    fetchJson<Cart>(`${BASE}/items/${index}`, {
      method: "PATCH",
      body: patch as unknown as Record<string, unknown>
    }),
  remove: (index: number): Promise<Cart> =>
    fetchJson<Cart>(`${BASE}/items/${index}`, { method: "DELETE" })
};

// POST /api/v1/checkout/finalize — the big one. The Idempotency-Key
// MUST be passed in the request headers; we generate one client-side
// per checkout attempt and persist it for the duration of the
// authorize call so a retry from a flaky network reuses it.
export async function finalizeCheckout(
  idempotencyKey: string,
  body: FinalizeRequest
): Promise<FinalizeResponse> {
  return fetchJson<FinalizeResponse>(FINALIZE, {
    method: "POST",
    body: body as unknown as Record<string, unknown>,
    headers: { "Idempotency-Key": idempotencyKey } as Record<string, string>
  });
}
